/**
 * Lista de conversaciones — col 1 del inbox y de la vista de conversación.
 *
 * Visual: header sticky con título + contador + density toggle + filters,
 * lista densa de cards estilo email client (no rounded, border-b). Estado
 * seleccionado con border-l 2px magenta y bg-surface-3.
 *
 * Reusable: la misma lista se monta en /inbox (col izq) y en
 * /inbox/$conversationId (col izq también, para no perder contexto al
 * navegar a una conversación).
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useConversations, type ConversationFilters } from "@/hooks/use-conversations";
import { useActiveClient } from "@/hooks/use-active-client";
import { useInboxStream } from "@/hooks/use-sse";
import { usePanelHotkey } from "@/hooks/use-hotkeys";
import { EmptyState } from "@/components/shared/empty-state";
import { InboxFilters } from "@/components/inbox/filters";
import { SlaPill } from "@/components/inbox/sla-pill";
import { Avatar } from "@/components/shared/avatar";
import { cn } from "@/lib/cn";
import { formatArrivalAge } from "@/lib/format";
import { isReclamo } from "@/lib/intent";
import type { Conversation, ConversationState, Platform } from "@/types";

interface ConversationsListProps {
  /** id de la conv seleccionada (para resaltar). undefined = lista inbox sin seleccion. */
  activeConversationId?: string;
}

const ROW_PAD = "py-3 px-4";

const PLATFORM_LABEL: Record<Platform | string, string> = {
  instagram: "IG",
  facebook: "FB",
  whatsapp: "WA",
};

const STATE_LABEL: Record<ConversationState, string> = {
  bot: "Bot",
  human: "Humano",
  escalated: "Escalada",
  closed: "Cerrada",
};

const STATE_CLASSES: Record<ConversationState, string> = {
  bot: "bg-state-bot-bg text-state-bot-text border border-state-bot-border",
  human: "bg-state-human-bg text-state-human-text border border-state-human-border",
  escalated:
    "bg-state-escalated-bg text-state-escalated-text border border-state-escalated-border",
  closed:
    "bg-state-closed-bg text-state-closed-text border border-state-closed-border",
};

export function ConversationsList({ activeConversationId }: ConversationsListProps) {
  const activeClient = useActiveClient();
  // SSE realtime — el endpoint solo expone canales per-cliente.
  useInboxStream(activeClient?.slug);
  const [filters, setFilters] = useState<ConversationFilters>({});
  // El cliente del path tiene prioridad: si estamos en /c/:slug/inbox, fija el filtro.
  const effectiveFilters: ConversationFilters = activeClient
    ? { ...filters, client: activeClient.slug }
    : filters;
  const { data: conversationsRaw, isLoading } = useConversations(effectiveFilters);

  // Counts pre-tab (sobre la lista filtrada por todo menos messageKind).
  const dmCount = useMemo(
    () =>
      conversationsRaw?.filter((c) => c.lastMessageKind === "dm").length ?? 0,
    [conversationsRaw],
  );
  const commentCount = useMemo(
    () =>
      conversationsRaw?.filter((c) => c.lastMessageKind === "comment").length ?? 0,
    [conversationsRaw],
  );

  // Filtro de tab aplicado en el componente para que los counts de arriba
  // queden estables.
  const kindFilter = filters.messageKind ?? "all";
  const conversations = useMemo(() => {
    if (!conversationsRaw) return conversationsRaw;
    if (kindFilter === "all") return conversationsRaw;
    return conversationsRaw.filter((c) => c.lastMessageKind === kindFilter);
  }, [conversationsRaw, kindFilter]);

  const escaladasCount = useMemo(
    () => conversations?.filter((c) => c.state === "escalated").length ?? 0,
    [conversations],
  );
  const overdueCount = useMemo(
    () => conversations?.filter((c) => c.slaRemainingSec < 0).length ?? 0,
    [conversations],
  );
  const total = conversations?.length ?? 0;

  // Hotkeys J/K para navegar entre convs del inbox. Si no hay activeConversationId,
  // J selecciona la primera; K la última.
  const navigate = useNavigate();
  const navigateTo = (target: Conversation) => {
    if (!activeClient) return;
    navigate({
      to: "/c/$clientSlug/inbox/$conversationId",
      params: { clientSlug: activeClient.slug, conversationId: target.id },
    });
  };
  usePanelHotkey(
    "nav-next",
    () => {
      if (!conversations?.length) return;
      const idx = activeConversationId
        ? conversations.findIndex((c) => c.id === activeConversationId)
        : -1;
      const next = conversations[Math.min(idx + 1, conversations.length - 1)];
      if (next && next.id !== activeConversationId) navigateTo(next);
    },
    !!conversations?.length,
  );
  usePanelHotkey(
    "nav-prev",
    () => {
      if (!conversations?.length) return;
      const idx = activeConversationId
        ? conversations.findIndex((c) => c.id === activeConversationId)
        : conversations.length;
      const prev = conversations[Math.max(idx - 1, 0)];
      if (prev && prev.id !== activeConversationId) navigateTo(prev);
    },
    !!conversations?.length,
  );

  return (
    <aside
      className={cn(
        // En md+ siempre visible al lado del thread.
        "md:flex md:w-conv-list",
        // En mobile: ocupa toda la pantalla cuando NO hay conv seleccionada;
        // se oculta cuando hay conv activa (el thread toma el lugar).
        activeConversationId ? "hidden" : "flex w-full",
        "flex-col border-r border-subtle bg-surface-1 min-h-0",
      )}
    >
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-surface-1 border-b border-subtle px-5 pt-5 pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="font-sans text-3xl font-semibold tracking-snug text-text-primary">
              Inbox
            </h1>
            {activeClient && (
              <p className="font-mono text-2xs uppercase tracking-wider text-text-muted mt-0.5">
                {activeClient.fullName}
              </p>
            )}
          </div>
        </div>
        <p className="font-sans text-sm text-text-muted mt-1 tabular-nums">
          {total} conversaciones
          {escaladasCount > 0 && (
            <>
              {" · "}
              <span className="text-magenta font-medium">{escaladasCount} escaladas</span>
            </>
          )}
        </p>

        {/* Tabs Mensajes / Comentarios — filtran por tipo del último msg */}
        <div className="mt-3">
          <KindTabs
            value={kindFilter}
            onChange={(k) => setFilters({ ...filters, messageKind: k })}
            counts={{
              all: (conversationsRaw?.length ?? 0),
              dm: dmCount,
              comment: commentCount,
            }}
          />
        </div>

        <div className="mt-3">
          <InboxFilters
            value={filters}
            onChange={setFilters}
            totalCount={total}
            overdueCount={overdueCount}
            hideClientFilter={!!activeClient}
          />
        </div>
      </header>

      {/* Lista scroll */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="px-5 py-8 text-sm text-text-muted">Cargando…</div>
        ) : !conversations?.length ? (
          <EmptyState
            title="Sin conversaciones"
            description="Cuando lleguen DMs o comentarios aparecen acá"
          />
        ) : (
          <ul role="list" className="flex flex-col">
            {conversations.map((conv) => (
              <li key={conv.id}>
                <ConversationCard
                  conv={conv}
                  active={conv.id === activeConversationId}
                  clientSlugInPath={activeClient?.slug}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

interface ConversationCardProps {
  conv: Conversation;
  active: boolean;
  /** Si presente, navega a /c/:slug/inbox/$id en lugar del legacy. */
  clientSlugInPath?: string;
}

function ConversationCard({
  conv,
  active,
  clientSlugInPath,
}: ConversationCardProps) {
  // El path siempre debe ser workspace-scoped. Si no estamos en uno,
  // usamos el slug de la propia conversación para llegar al thread.
  const targetSlug = clientSlugInPath ?? conv.client;
  // No leída = ningún humano la revisó desde el último mensaje. Incluye las
  // respondidas por el CM AI: siguen no-leídas hasta que un humano abra el thread.
  const isUnread = conv.unread > 0;
  return (
    <Link
      to="/c/$clientSlug/inbox/$conversationId"
      params={{ clientSlug: targetSlug, conversationId: conv.id }}
      className={cn(
        "relative block border-b border-subtle transition-colors",
        ROW_PAD,
        "hover:bg-surface-2",
        active && "bg-surface-3",
      )}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-[2px] bg-magenta"
        />
      )}

      <div className="flex items-start gap-3">
        {/* Avatar — foto real de IG si está disponible, iniciales de fallback. */}
        <Avatar name={conv.externalUserName} src={conv.externalUserAvatar} size={32} />

        <div className="flex-1 min-w-0">
          {/* Row 1: nombre + handle */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {isUnread && (
                <span
                  aria-hidden="true"
                  title="No leído"
                  className="w-2 h-2 rounded-full bg-magenta shrink-0"
                />
              )}
              <span
                className={cn(
                  "font-sans text-sm truncate",
                  isUnread
                    ? "font-semibold text-text-primary"
                    : "font-normal text-text-secondary",
                )}
              >
                {conv.externalUserName}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className="font-mono text-[11px] tabular-nums text-text-muted"
                title={`Llegó: ${new Date(conv.lastMessageAt).toLocaleString("es-AR")}`}
              >
                {formatArrivalAge(conv.lastMessageAt)}
              </span>
            </div>
          </div>

          {/* Row 2: badges */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className="inline-flex items-center rounded-full border border-default bg-surface-2 px-2 py-0.5 text-2xs font-sans font-medium text-text-secondary">
              {PLATFORM_LABEL[conv.platform] ?? conv.platform}
            </span>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-sans font-medium",
                conv.lastMessageKind === "comment"
                  ? "bg-state-bot-bg text-state-bot-text border border-state-bot-border"
                  : "bg-surface-2 text-text-secondary border border-default",
              )}
              title={conv.lastMessageKind === "comment" ? "Comentario en post" : "Mensaje directo"}
            >
              {conv.lastMessageKind === "comment" ? "Comentario" : "DM"}
            </span>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-sans font-medium",
                STATE_CLASSES[conv.state],
              )}
            >
              {STATE_LABEL[conv.state]}
            </span>
            {/* SLA de respuesta — solo para conversaciones que maneja el bot.
                Escaladas y cerradas NO muestran SLA de respuesta: la escalada
                fue a propósito, mostrar "Vencido" en rojo sería engañoso (el
                badge "Escalada" ya comunica el estado). */}
            {conv.state !== "closed" && conv.state !== "escalated" && (
              <SlaPill remainingSec={conv.slaRemainingSec} />
            )}
            {/* Autoetiquetado de reclamo (R3.5) — heurística sobre el último mensaje. */}
            {isReclamo(conv.lastMessagePreview) && (
              <span
                title="Posible reclamo de producto (autoetiquetado)"
                className="inline-flex items-center gap-1 rounded-full border border-error-border bg-error-bg px-2 py-0.5 text-2xs font-sans font-medium text-error-text"
              >
                reclamo
              </span>
            )}
          </div>

          {/* Row 3: preview — atenuado cuando ya fue leída por un humano. */}
          <p
            className={cn(
              "mt-1.5 font-sans text-sm line-clamp-2 leading-snug",
              isUnread ? "text-text-secondary" : "text-text-muted",
            )}
          >
            {conv.lastMessagePreview || (
              <span className="text-text-muted">Sin mensajes</span>
            )}
          </p>

          {/* Row 4: cliente */}
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className="font-sans text-xs text-text-muted truncate">
              {conv.client}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

interface KindTabsProps {
  value: "all" | "dm" | "comment";
  onChange: (v: "all" | "dm" | "comment") => void;
  counts: { all: number; dm: number; comment: number };
}

function KindTabs({ value, onChange, counts }: KindTabsProps) {
  const tabs: Array<{ value: "all" | "dm" | "comment"; label: string }> = [
    { value: "all", label: "Todos" },
    { value: "dm", label: "Mensajes" },
    { value: "comment", label: "Comentarios" },
  ];
  return (
    <div
      role="tablist"
      aria-label="Tipo de conversación"
      className="inline-flex items-center rounded-full bg-surface-2 border border-default p-[3px] w-full"
    >
      {tabs.map((t) => {
        const active = value === t.value;
        const count = counts[t.value];
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            className={cn(
              "flex-1 h-7 px-3 rounded-full text-xs font-sans font-medium",
              "inline-flex items-center justify-center gap-1.5 transition-colors",
              active
                ? "bg-surface-1 text-text-primary shadow-sm"
                : "text-text-secondary hover:text-text-primary",
            )}
          >
            {t.label}
            <span className="font-mono tabular-nums text-2xs text-text-muted">
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
